import React, { useState } from 'react'
import {
  Zap,
  CreditCard,
  ShieldAlert,
  Receipt,
  MessageSquare,
  RefreshCw,
  FileText,
  CheckCircle,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Info,
  Ellipsis,
} from 'lucide-react'
import { cn } from '@shared/lib/utils'
import { Button } from '@shared/components/ui/button'
import type { ActivityEvent, ActivityEventIcon } from './types'
import { isActionRequired } from './types'

// ─────────────────────────────────────────────────────────────────────────────
// Icon mapping
// ─────────────────────────────────────────────────────────────────────────────

const ICON_MAP: Record<ActivityEventIcon, React.FC<{ className?: string }>> = {
  'zap': Zap,
  'credit-card': CreditCard,
  'shield-alert': ShieldAlert,
  'receipt': Receipt,
  'message-square': MessageSquare,
  'refresh-cw': RefreshCw,
  'file-text': FileText,
  'check-circle': CheckCircle,
  'arrow-left': ArrowLeft,
}

// ─────────────────────────────────────────────────────────────────────────────
// Severity-based style tokens
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  warning: { circleBg: 'bg-amber-50', iconColor: 'text-amber-600', dotBg: 'bg-amber-600' },
  error:   { circleBg: 'bg-red-50',   iconColor: 'text-red-500',   dotBg: 'bg-red-500'   },
  info:    { circleBg: 'bg-muted',    iconColor: 'text-muted-foreground', dotBg: 'bg-muted-foreground' },
} as const

function getSeverityKey(event: ActivityEvent): keyof typeof SEVERITY_STYLES {
  const needsAction = isActionRequired(event) && !event.actionCompleted
  if (needsAction || event.severity === 'warning') return 'warning'
  if (event.severity === 'error') return 'error'
  return 'info'
}

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface ActivityItemProps {
  event: ActivityEvent
  isLast?: boolean
  showLine?: boolean
  onActionClick?: (event: ActivityEvent) => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const ActivityItem: React.FC<ActivityItemProps> = ({
  event,
  isLast = false,
  showLine = true,
  onActionClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    return isActionRequired(event) && !event.actionCompleted
  })

  const needsAction = isActionRequired(event) && !event.actionCompleted
  const hasExpandableContent = Boolean(
    event.subEvents?.length || event.statusCard || event.details?.length || event.operatorResponse
  )

  const styles = SEVERITY_STYLES[getSeverityKey(event)]
  const IconComponent = event.icon ? ICON_MAP[event.icon] : null

  const toggleExpand = () => {
    if (hasExpandableContent) {
      setIsExpanded((p) => !p)
    }
  }

  return (
    <div className="flex gap-2.5">
      {/* ── Timeline column: icon/dot + connecting lines ── */}
      <div className="flex flex-col items-center shrink-0 w-8">
        {/* Main icon circle */}
        <div className={cn('w-8 h-8 rounded-2xl flex items-center justify-center shrink-0', styles.circleBg)}>
          {IconComponent ? (
            <IconComponent className={cn('size-[14px]', styles.iconColor)} />
          ) : (
            <div className={cn('w-2 h-2 rounded-full', styles.dotBg)} />
          )}
        </div>

        {/* Connector line after icon */}
        {(showLine || (isExpanded && hasExpandableContent)) && !isLast && (
          <div className="w-px bg-border flex-1 min-h-[8px]" />
        )}

        {/* Sub-event dots + lines (when expanded) */}
        {isExpanded && event.subEvents?.map((sub, idx) => (
          <React.Fragment key={sub.id}>
            <div className="relative flex items-center justify-center shrink-0">
              <div className={cn('w-[18px] h-[18px] rounded-full blur-[1px] absolute', styles.circleBg)} />
              <div className={cn('w-[9px] h-[9px] rounded-full relative z-10', styles.dotBg)} />
            </div>
            {(idx < event.subEvents!.length - 1 || !isLast) && (
              <div className="w-px bg-border flex-1 min-h-[8px]" />
            )}
          </React.Fragment>
        ))}

        {/* Bottom connector for collapsed items with pending siblings */}
        {!isExpanded && showLine && !isLast && !event.subEvents?.length && (
          <div className="w-px bg-border flex-1 min-h-[8px]" />
        )}
      </div>

      {/* ── Content column ── */}
      <div className="flex-1 min-w-0 pb-8">
        {/* Title row with expand toggle */}
        <div
          className={cn(
            'flex items-start gap-2',
            hasExpandableContent && 'cursor-pointer'
          )}
          onClick={toggleExpand}
          role={hasExpandableContent ? 'button' : undefined}
          tabIndex={hasExpandableContent ? 0 : undefined}
          aria-expanded={hasExpandableContent ? isExpanded : undefined}
          onKeyDown={
            hasExpandableContent
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleExpand()
                  }
                }
              : undefined
          }
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-[14px] font-semibold text-foreground leading-5 tracking-[-0.14px]">
              {event.title}
            </span>
            <span className="text-[12px] text-muted-foreground leading-4 tracking-[-0.12px]">
              {event.timestampDisplay}
            </span>
          </div>
          {hasExpandableContent && (
            <div className="pt-[3px]">
              {isExpanded ? (
                <ChevronUp className="size-6 text-muted-foreground" />
              ) : (
                <ChevronDown className="size-6 text-muted-foreground" />
              )}
            </div>
          )}
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="flex flex-col gap-3 mt-3">
            {/* Status card (e.g. dispute "Initiated" details) */}
            {event.statusCard && (
              <div className="max-w-[70%] rounded-2xl overflow-hidden">
                <div className="bg-muted px-3 py-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-semibold text-foreground leading-4">
                      {event.statusCard.title}
                    </span>
                    <span className="text-[10px] text-muted-foreground leading-4 tracking-[-0.1px]">
                      {event.statusCard.timestampDisplay}
                    </span>
                    <Info className="size-[12px] text-muted-foreground shrink-0" />
                  </div>
                  {event.statusCard.description && (
                    <p className="text-[10px] text-muted-foreground leading-4 tracking-[-0.1px] mt-0.5">
                      {event.statusCard.description}
                    </p>
                  )}
                </div>

                {/* Win probability bar */}
                {event.winProbability != null && (
                  <div className="bg-emerald-50 flex items-center justify-center gap-1 px-3 py-1.5 h-7">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                    <span className="text-[11px] text-emerald-600 leading-4">
                      <span className="font-bold">{event.winProbability}%</span>{' '}
                      <span className="font-medium">win probability</span>
                    </span>
                    <Info className="size-[12px] text-emerald-600 shrink-0" />
                  </div>
                )}
              </div>
            )}

            {/* Action buttons row */}
            {needsAction && (
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-full px-3 text-[12px] font-medium shadow-[0px_10px_28px_0px_rgba(11,59,69,0.1),0px_1px_2px_0px_rgba(11,59,69,0.06)]"
                  onClick={(e) => {
                    e.stopPropagation()
                    onActionClick?.(event)
                  }}
                >
                  {isActionRequired(event) ? event.actionLabel : ''}
                </Button>

                {event.additionalActions?.map((action, idx) => (
                  <Button
                    key={idx}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-full px-3 text-[14px] font-medium shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {action.variant === 'more' ? <Ellipsis className="size-4" /> : action.label}
                  </Button>
                ))}
              </div>
            )}

            {/* Sub-events list */}
            {event.subEvents && event.subEvents.length > 0 && (
              <div className="flex flex-col gap-4 mt-3">
                {event.subEvents.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-1.5 h-5">
                    <span className="text-[12px] font-medium text-foreground leading-6">
                      {sub.title}
                    </span>
                    <span className="text-[12px] text-muted-foreground leading-4 tracking-[-0.12px]">
                      {sub.timestampDisplay}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Legacy details */}
            {event.details && event.details.length > 0 && !event.subEvents?.length && (
              <div className="space-y-2 bg-muted rounded-xl p-3">
                {event.details.map((detail, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-[12px] font-medium text-foreground">{detail.action}</span>
                    <span className="text-[12px] text-muted-foreground ml-2 shrink-0">
                      {detail.timestamp}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Operator response bubble — below incoming, left-aligned */}
            {event.operatorResponse && (
              <div className="max-w-[70%] bg-muted rounded-2xl px-3 py-2">
                <p className="text-[12px] font-semibold text-foreground leading-4">
                  {event.operatorResponse.label}
                </p>
                {event.operatorResponse.notes && (
                  <p className="text-[11px] text-muted-foreground leading-4 mt-0.5">
                    {event.operatorResponse.notes}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground mt-1 tracking-[-0.1px]">
                  {event.operatorResponse.actor} · {event.operatorResponse.timestampDisplay}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
